import type { RadioGroupProps } from "@mui/material"
import { FormControl, FormControlLabel, FormHelperText, InputLabel, Radio, RadioGroup } from "@mui/material"
import { type ReactElement, forwardRef, useId } from "react"

import type { Option } from "@/types/form-fields"

export interface AerosRadioGroupProps<TValue> extends Omit<RadioGroupProps, "onChange"> {
  options: Option<TValue>[]
  label?: string
  helperText?: string
  required?: boolean
  error?: boolean
  disabled?: boolean
  onChange?: (value: TValue) => void
  "data-testid"?: string
}

export const AerosRadioGroup = forwardRef(
  <TValue,>(
    {
      options,
      label,
      helperText,
      required,
      error,
      disabled,
      className,
      onChange,
      "data-testid": testId,
      ...props
    }: AerosRadioGroupProps<TValue>,
    ref: React.Ref<HTMLDivElement>
  ): ReactElement => {
    const labelId = useId()

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value as TValue)
    }

    return (
      <FormControl
        ref={ref}
        className={className}
        required={required}
        error={error}
        disabled={disabled}
        data-testid={testId}
      >
        {label && <InputLabel id={labelId}>{label}</InputLabel>}
        <RadioGroup {...props} aria-labelledby={label ? labelId : undefined} onChange={handleChange}>
          {options.map((option) => (
            <FormControlLabel
              key={String(option.value)}
              value={option.value}
              control={<Radio />}
              label={option.label}
              disabled={option.disabled}
            />
          ))}
        </RadioGroup>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    )
  }
) as <TValue>(props: AerosRadioGroupProps<TValue> & { ref?: React.Ref<HTMLDivElement> }) => ReactElement
